import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin,
  Key,
  SignOut,
  Desktop,
  CaretRight,
  Eye,
  BellRinging,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CalendarSubscribe } from "@/components/CalendarSubscribe";

export default function MobileSettings() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [editRoute, setEditRoute] = useState(false);
  const [route, setRoute] = useState(user?.route_start || "");
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [busy, setBusy] = useState(false);
  const { role } = usePermissions();
  const isAdmin = role === "admin";
  const [remOpen, setRemOpen] = useState(false);
  const [rem, setRem] = useState(null);
  const [remBusy, setRemBusy] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/settings/reminders")
      .then(({ data }) =>
        setRem({
          enabled: data.customer_enabled === true,
          time: data.customer_time || "08:00",
          lead: data.customer_lead_days == null ? 1 : data.customer_lead_days,
          stages: data.customer_stages || ["Template", "Install"],
          raw: data,
        }),
      )
      .catch(() => {});
  }, [isAdmin]);
  const saveReminders = async () => {
    setRemBusy(true);
    try {
      await api.put("/settings/reminders", {
        ...rem.raw,
        customer_enabled: rem.enabled,
        customer_time: rem.time,
        customer_lead_days: rem.lead,
        customer_stages: rem.stages,
      });
      toast.success("Reminder settings saved");
      setRemOpen(false);
    } catch {
      toast.error("Could not save reminders");
    }
    setRemBusy(false);
  };
  const [showDone, setShowDone] = useState(
    () => localStorage.getItem("mobile_show_done") === "1",
  );
  const toggleShowDone = () => {
    setShowDone((v) => {
      const next = !v;
      localStorage.setItem("mobile_show_done", next ? "1" : "0");
      return next;
    });
  };

  const saveRoute = async () => {
    setBusy(true);
    try {
      await api.post("/auth/me/route-start", { route_start: route });
      await refreshUser?.();
      toast.success("Route start saved");
      setEditRoute(false);
    } catch {
      toast.error("Could not save route start");
    }
    setBusy(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
      setPwOpen(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update password",
      );
    }
    setBusy(false);
  };

  const openDesktop = () => {
    localStorage.setItem("sw_mobile_mode", "0");
    navigate("/");
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const rowBtn =
    "flex w-full items-center gap-3 rounded-2xl border border-zinc-800 bg-[#0F172A] p-4 text-left transition-colors duration-150 hover:bg-zinc-800/60";
  const pwField =
    "h-12 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-base text-zinc-100 focus:border-[#c39e6d] focus:outline-none";

  return (
    <div className="px-4 pt-5" data-testid="mobile-settings">
      <h1 className="m-title m-display mb-4 text-5xl text-[#c39e6d]">Settings</h1>
      {/* Profile card */}
      <div
        className="flex items-center gap-3 rounded-2xl border border-zinc-800 bg-[#0F172A] p-4"
        data-testid="mobile-profile-card"
      >
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#c39e6d] text-lg font-bold uppercase text-slate-950">
          {(user?.name || user?.email || "?").slice(0, 2)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-base font-bold text-zinc-100">
            {user?.name || "Crew"}
          </div>
          <div
            className="truncate text-sm text-zinc-500"
            data-testid="mobile-settings-email"
          >
            {user?.email}
          </div>
        </div>
      </div>

      <div className="mt-5 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
        Profile
      </div>
      <div className="mt-2 space-y-2">
        {/* Route starting point */}
        <div className="rounded-2xl border border-zinc-800 bg-[#0F172A]">
          <button
            data-testid="mobile-settings-route-start"
            onClick={() => setEditRoute((v) => !v)}
            className="flex w-full items-center gap-3 p-4 text-left"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#c39e6d]">
              <MapPin size={17} weight="bold" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-zinc-100">
                Route Starting Point
              </span>
              <span
                className="block truncate text-xs text-zinc-500"
                data-testid="mobile-route-start-value"
              >
                {user?.route_start || "Using company location"}
              </span>
            </span>
            <CaretRight
              size={16}
              weight="bold"
              className={`shrink-0 text-zinc-500 transition-transform duration-150 ${
                editRoute ? "rotate-90" : ""
              }`}
            />
          </button>
          {editRoute && (
            <div className="border-t border-zinc-800 p-4">
              <input
                data-testid="mobile-route-start-input"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                placeholder="Shop address or starting point"
                className={pwField}
              />
              <button
                data-testid="mobile-route-start-save"
                onClick={saveRoute}
                disabled={busy}
                className="mt-3 h-11 w-full rounded-xl bg-[#c39e6d] text-xs font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="rounded-2xl border border-zinc-800 bg-[#0F172A]">
          <button
            data-testid="mobile-change-password-toggle"
            onClick={() => setPwOpen((v) => !v)}
            className="flex w-full items-center gap-3 p-4 text-left"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#c39e6d]">
              <Key size={17} weight="bold" />
            </span>
            <span className="flex-1 text-sm font-semibold text-zinc-100">
              Change Password
            </span>
            <CaretRight
              size={16}
              weight="bold"
              className={`shrink-0 text-zinc-500 transition-transform duration-150 ${
                pwOpen ? "rotate-90" : ""
              }`}
            />
          </button>
          {pwOpen && (
            <form
              onSubmit={changePw}
              className="space-y-3 border-t border-zinc-800 p-4"
              data-testid="mobile-password-form"
            >
              <input
                data-testid="mobile-pw-current"
                type="password"
                required
                placeholder="Current password"
                value={pw.current_password}
                onChange={(e) =>
                  setPw({ ...pw, current_password: e.target.value })
                }
                className={pwField}
              />
              <input
                data-testid="mobile-pw-new"
                type="password"
                required
                minLength={6}
                placeholder="New password"
                value={pw.new_password}
                onChange={(e) =>
                  setPw({ ...pw, new_password: e.target.value })
                }
                className={pwField}
              />
              <input
                data-testid="mobile-pw-confirm"
                type="password"
                required
                minLength={6}
                placeholder="Confirm new password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className={pwField}
              />
              <button
                data-testid="mobile-pw-submit"
                type="submit"
                disabled={busy}
                className="h-11 w-full rounded-xl bg-[#c39e6d] text-xs font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-50"
              >
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>

        {/* Log out */}
        <button
          data-testid="mobile-settings-logout"
          onClick={doLogout}
          className={rowBtn}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-500/15 text-red-400">
            <SignOut size={17} weight="bold" />
          </span>
          <span className="flex-1 text-sm font-semibold text-red-400">
            Log Out
          </span>
        </button>
      </div>

      <div className="mt-2">
        <CalendarSubscribe mobile />
      </div>

      <div className="mt-5 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
        Preferences
      </div>
      <div className="mt-2">
        <button
          type="button"
          data-testid="mobile-settings-show-done"
          onClick={toggleShowDone}
          className={rowBtn}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#c39e6d]">
            <Eye size={17} weight="bold" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-zinc-100">
              Show Completed Jobs
            </span>
            <span className="block truncate text-xs text-zinc-500">
              {showDone
                ? "Completed stops appear in your day, schedule and map"
                : "Completed stops are hidden across the app"}
            </span>
          </span>
          <span
            data-testid="mobile-settings-show-done-switch"
            aria-checked={showDone}
            role="switch"
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${
              showDone ? "bg-[#c39e6d]" : "bg-zinc-700"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-200 ${
                showDone ? "translate-x-[22px]" : "translate-x-0.5"
              }`}
            />
          </span>
        </button>
      </div>

      {isAdmin && rem && (
        <>
          <div className="mt-5 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
            Automations
          </div>
          <div
            className="mt-2 rounded-2xl border border-zinc-800 bg-[#0F172A]"
            data-testid="mobile-reminder-settings"
          >
            <button
              data-testid="mobile-reminder-toggle-open"
              onClick={() => setRemOpen((v) => !v)}
              className="flex w-full items-center gap-3 p-4 text-left"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#c39e6d]">
                <BellRinging size={17} weight="bold" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-zinc-100">
                  Appointment Reminders
                </span>
                <span
                  className="block truncate text-xs text-zinc-500"
                  data-testid="mobile-reminder-summary"
                >
                  {rem.enabled
                    ? `On · ${rem.lead}d before · ${rem.time}`
                    : "Off — customers aren't auto-texted"}
                </span>
              </span>
              <CaretRight
                size={16}
                weight="bold"
                className={`shrink-0 text-zinc-500 transition-transform duration-150 ${
                  remOpen ? "rotate-90" : ""
                }`}
              />
            </button>
            {remOpen && (
              <div className="space-y-4 border-t border-zinc-800 p-4">
                <button
                  type="button"
                  data-testid="mobile-reminder-enabled"
                  onClick={() => setRem((r) => ({ ...r, enabled: !r.enabled }))}
                  className={`h-11 w-full rounded-xl border text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${
                    rem.enabled
                      ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                      : "border-zinc-800 bg-[#080C14] text-zinc-500"
                  }`}
                >
                  Auto-text customers {rem.enabled ? "On" : "Off"}
                </button>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Send at
                  </label>
                  <input
                    type="time"
                    data-testid="mobile-reminder-time"
                    value={rem.time}
                    onChange={(e) =>
                      setRem((r) => ({ ...r, time: e.target.value }))
                    }
                    className={pwField}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Days before
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={14}
                    data-testid="mobile-reminder-lead"
                    value={rem.lead}
                    onChange={(e) =>
                      setRem((r) => ({
                        ...r,
                        lead: Math.max(
                          0,
                          Math.min(14, Number(e.target.value) || 0),
                        ),
                      }))
                    }
                    className={pwField}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Remind about
                  </label>
                  <div
                    className="mt-1 flex flex-wrap gap-2"
                    data-testid="mobile-reminder-stages"
                  >
                    {["Template", "Fabrication", "Install"].map((st) => {
                      const on = rem.stages.includes(st);
                      return (
                        <button
                          key={st}
                          type="button"
                          data-testid={`mobile-reminder-stage-${st}`}
                          onClick={() =>
                            setRem((r) => ({
                              ...r,
                              stages: on
                                ? r.stages.filter((x) => x !== st)
                                : [...r.stages, st],
                            }))
                          }
                          className={`h-9 rounded-lg border px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                            on
                              ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                              : "border-zinc-800 bg-[#080C14] text-zinc-500"
                          }`}
                        >
                          {st}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  data-testid="mobile-reminder-save"
                  onClick={saveReminders}
                  disabled={remBusy}
                  className="h-11 w-full rounded-xl bg-[#c39e6d] text-xs font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-50"
                >
                  {remBusy ? "Saving…" : "Save"}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-5 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
        Other
      </div>
      <div className="mt-2">
        <button
          data-testid="mobile-open-desktop"
          onClick={openDesktop}
          className={rowBtn}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#c39e6d]">
            <Desktop size={17} weight="bold" />
          </span>
          <span className="flex-1 text-sm font-semibold text-zinc-100">
            Open Desktop App
          </span>
          <CaretRight size={16} weight="bold" className="text-zinc-500" />
        </button>
      </div>

      <p className="mt-6 text-center text-[11px] text-zinc-600">
        Brioni Granite Field · v1.0
      </p>
    </div>
  );
}
